import { useEffect, useCallback, useRef } from 'react';
import { DURATION_LABELS_LAYER } from '../constants';

// Hook to automatically hide duration labels at city zoom levels
export const useAutoHideDurationLabels = (
  map, 
  mapReady,
  userControlledVisibility,
  onAutoVisibilityChange,
  onUserPreferenceChange
) => {
  // City zoom threshold
  const CITY_ZOOM_THRESHOLD = 8;
  
  // Track auto-hide state
  const autoHiddenRef = useRef(false);
  const lastZoomRef = useRef(null);
  const userPreferenceRef = useRef(userControlledVisibility);
  
  // Keep user preference in sync
  useEffect(() => {
    userPreferenceRef.current = userControlledVisibility;
  }, [userControlledVisibility]);

  // Determine if labels should be hidden at this zoom
  const shouldAutoHide = useCallback((zoom) => {
    return zoom <= CITY_ZOOM_THRESHOLD;
  }, []);

  // Update label visibility based on zoom level
  const updateDurationLabelsVisibility = useCallback((zoom, force = false) => {
    if (!map || !mapReady) return;

    const shouldHide = shouldAutoHide(zoom);
    
    // Skip if no threshold crossing and not forced
    if (!force && 
        lastZoomRef.current !== null && 
        shouldAutoHide(lastZoomRef.current) === shouldHide) {
      return;
    }

    try {
      if (map.getLayer(DURATION_LABELS_LAYER)) {
        let finalVisibility;
        
        if (shouldHide) {
          // Force hide at city level
          finalVisibility = 'none';
          autoHiddenRef.current = true; // Auto-hidden
        } else {
          // At non-city level - auto-enable if previously hidden
          if (autoHiddenRef.current) {
            userPreferenceRef.current = true;
            // Notify layer control
            if (onUserPreferenceChange) {
              onUserPreferenceChange(true);
            }
          }
          
          finalVisibility = userPreferenceRef.current ? 'visible' : 'none';
          autoHiddenRef.current = false;
        }

        map.setLayoutProperty(DURATION_LABELS_LAYER, 'visibility', finalVisibility);

        // Notify components of visibility changes
        if (onAutoVisibilityChange) {
          onAutoVisibilityChange({
            zoom,
            shouldAutoHide: shouldHide,
            isAutoHidden: autoHiddenRef.current,
            finalVisibility: finalVisibility === 'visible'
          });
        }
      }
    } catch (error) {
      console.error('Error updating duration labels visibility:', error);
    }

    lastZoomRef.current = zoom;
  }, [map, mapReady, shouldAutoHide, onAutoVisibilityChange, onUserPreferenceChange]);

  // Handle map zoom events
  useEffect(() => {
    if (!map || !mapReady) return;

    const handleZoomEnd = () => {
      const currentZoom = map.getZoom();
      updateDurationLabelsVisibility(currentZoom);
    };

    // Check initial zoom level
    if (map.loaded()) {
      const currentZoom = map.getZoom();
      updateDurationLabelsVisibility(currentZoom, true);
    }

    map.on('zoomend', handleZoomEnd);
    
    return () => {
      map.off('zoomend', handleZoomEnd);
    };
  }, [map, mapReady, updateDurationLabelsVisibility]);

  // Update when user preference changes
  useEffect(() => {
    if (map && mapReady && lastZoomRef.current !== null) {
      updateDurationLabelsVisibility(lastZoomRef.current, true);
    }
  }, [map, mapReady, userControlledVisibility, updateDurationLabelsVisibility]);

  // Return hook interface
  return {
    isAutoHidden: autoHiddenRef.current,
    cityZoomThreshold: CITY_ZOOM_THRESHOLD,
    shouldAutoHide: shouldAutoHide,
    forceUpdateVisibility: () => {
      if (map && mapReady) {
        const currentZoom = map.getZoom();
        updateDurationLabelsVisibility(currentZoom, true);
      }
    }
  };
};

